import React, { useState, useCallback, useRef } from 'react';
import ReactECharts from 'echarts-for-react';
import { usePortalContext } from '@/layouts/CustomerPortalLayout';
import { parseFile } from '@/utils/loadProfileParser';
import { formatConsumption } from '@/utils/formatters';
import {
  Upload, FileText, BarChart3, Zap, Sun, Moon, TrendingUp, AlertCircle,
  CheckCircle2, Calendar, Clock, Info, Download, ArrowDown, ArrowUp
} from 'lucide-react';

const WEEKDAYS_SHORT = ['So', 'Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa'];
const WEEKDAYS = ['Sonntag', 'Montag', 'Dienstag', 'Mittwoch', 'Donnerstag', 'Freitag', 'Samstag'];

const PortalConsumption = () => {
  const { data } = usePortalContext();
  const company = data.company;
  const [uploadedFile, setUploadedFile] = useState(null);
  const [analysis, setAnalysis] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState(null);
  const [dragActive, setDragActive] = useState(false);
  const inputRef = useRef(null);

  const handleDrag = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') setDragActive(true);
    else if (e.type === 'dragleave') setDragActive(false);
  }, []);

  const processFile = async (file) => {
    setUploadedFile(file);
    setError(null);
    setProcessing(true);
    try {
      const parsed = await parseFile(file);
      setAnalysis(buildAnalysis(parsed));
    } catch (err) {
      setError(err.message);
    } finally {
      setProcessing(false);
    }
  };

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setDragActive(false);
    if (e.dataTransfer.files?.[0]) processFile(e.dataTransfer.files[0]);
  }, []);

  const buildAnalysis = (parsed) => {
    const { data, totalKwh, peakKwh, offPeakKwh, dataPoints, intervalMinutes } = parsed;
    const hourly = Array(24).fill(null).map(() => ({ total: 0, count: 0 }));
    const weekday = Array(7).fill(null).map(() => ({ total: 0, count: 0 }));
    const heatmap = Array(7).fill(null).map(() => Array(24).fill(null).map(() => ({ total: 0, count: 0 })));

    let maxValue = 0;
    data.forEach(d => {
      const hour = d.time.getHours();
      const dayIdx = d.time.getDay();
      hourly[hour].total += d.value; hourly[hour].count++;
      weekday[dayIdx].total += d.value; weekday[dayIdx].count++;
      heatmap[dayIdx][hour].total += d.value; heatmap[dayIdx][hour].count++;
      if (d.value > maxValue) maxValue = d.value;
    });

    const hourlyAvg = hourly.map(h => h.count > 0 ? h.total / h.count : 0);
    const weekdayAvg = weekday.map(w => w.count > 0 ? w.total / w.count : 0);
    const baseLoad = Math.min(...hourlyAvg.filter(v => v > 0));
    const peakLoad = Math.max(...hourlyAvg);
    const loadFactor = peakLoad > 0 ? (hourlyAvg.reduce((s, v) => s + v, 0) / 24) / peakLoad : 0;

    const totalDays = new Set(data.map(d => d.time.toISOString().slice(0, 10))).size;
    const avgDaily = totalDays > 0 ? totalKwh / totalDays : 0;
    const annualKwh = avgDaily * 365;

    return {
      totalKwh, peakKwh, offPeakKwh,
      peakPercent: totalKwh > 0 ? (peakKwh / totalKwh) * 100 : 0,
      dataPoints, intervalMinutes,
      hourlyAvg, weekdayAvg, heatmapData: heatmap,
      baseLoad, peakLoad, loadFactor,
      totalDays, avgDaily, annualKwh, maxValue,
    };
  };

  // Chart options — simplified for customer view
  const hourlyChartOption = !analysis ? null : {
    tooltip: { trigger: 'axis', formatter: (p) => `${p[0].name}:00 Uhr<br/>${p[0].value.toFixed(2)} kW` },
    grid: { left: '3%', right: '4%', bottom: '3%', containLabel: true },
    xAxis: { type: 'category', data: Array.from({ length: 24 }, (_, i) => String(i).padStart(2, '0')), axisLabel: { color: '#64748b' }, axisLine: { lineStyle: { color: '#e7e9eb' } } },
    yAxis: { type: 'value', name: 'kW', axisLabel: { color: '#64748b' }, splitLine: { lineStyle: { color: '#f1f5f9' } } },
    series: [{
      type: 'bar',
      data: analysis.hourlyAvg.map((v, i) => ({ value: v, itemStyle: { color: (i >= 8 && i < 20) ? '#FF9100' : '#007D88', borderRadius: [4, 4, 0, 0] } })),
      barWidth: '70%'
    }]
  };

  const weekdayChartOption = !analysis ? null : {
    tooltip: { trigger: 'axis', formatter: (p) => `${p[0].name}<br/>${p[0].value.toFixed(2)} kW Durchschnitt` },
    grid: { left: '3%', right: '4%', bottom: '3%', containLabel: true },
    xAxis: { type: 'category', data: WEEKDAYS_SHORT, axisLabel: { color: '#64748b' }, axisLine: { lineStyle: { color: '#e7e9eb' } } },
    yAxis: { type: 'value', name: 'kW', axisLabel: { color: '#64748b' }, splitLine: { lineStyle: { color: '#f1f5f9' } } },
    series: [{ type: 'bar', data: analysis.weekdayAvg, itemStyle: { color: '#007D88', borderRadius: [4, 4, 0, 0] }, barWidth: '60%' }]
  };

  const heatmapOption = !analysis ? null : (() => {
    const heatData = [];
    analysis.heatmapData.forEach((day, d) => day.forEach((hour, h) => {
      if (hour.count > 0) heatData.push([h, d, +(hour.total / hour.count).toFixed(2)]);
    }));
    const values = heatData.map(d => d[2]);
    return {
      tooltip: { formatter: (p) => `${WEEKDAYS[p.value[1]]}, ${p.value[0]}:00 Uhr<br/>${p.value[2]} kW` },
      grid: { left: '10%', right: '5%', top: '5%', bottom: '15%', containLabel: true },
      xAxis: { type: 'category', data: Array.from({ length: 24 }, (_, i) => String(i).padStart(2, '0') + 'h'), axisLabel: { color: '#64748b', fontSize: 10 }, splitArea: { show: true } },
      yAxis: { type: 'category', data: WEEKDAYS_SHORT, axisLabel: { color: '#64748b' }, splitArea: { show: true } },
      visualMap: {
        min: Math.min(...values), max: Math.max(...values),
        calculable: true, orient: 'horizontal', left: 'center', bottom: '0',
        inRange: { color: ['#e6f2f3', '#007D88', '#004449'] },
        textStyle: { color: '#64748b' }
      },
      series: [{ type: 'heatmap', data: heatData, emphasis: { itemStyle: { shadowBlur: 10, shadowColor: 'rgba(0,0,0,0.3)' } } }]
    };
  })();

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-foreground tracking-tight">Ihre Verbrauchsanalyse</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Verstehen Sie wie Ihr Unternehmen Energie verbraucht — und finden Sie Optimierungspotenzial.
        </p>
      </div>

      {/* Profile summary */}
      <div className="hk-card">
        <div className="hk-card-body">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div>
              <p className="text-xs text-muted-foreground font-semibold uppercase tracking-wider">Ihr Unternehmen</p>
              <p className="text-sm font-bold text-foreground mt-1">{company?.name || '–'}</p>
              <p className="text-xs text-muted-foreground">{company?.industry || 'Branche nicht angegeben'}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground font-semibold uppercase tracking-wider">Jahresverbrauch</p>
              <p className="text-sm font-bold text-foreground mt-1">{company?.annual_consumption_kwh ? formatConsumption(company.annual_consumption_kwh) : '–'}</p>
              <p className="text-xs text-muted-foreground">{company?.annual_consumption_kwh >= 100000 ? 'RLM-Kunde' : 'SLP-Kunde'}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground font-semibold uppercase tracking-wider">Standort</p>
              <p className="text-sm font-bold text-foreground mt-1">{company?.plz} {company?.city}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground font-semibold uppercase tracking-wider">Aktueller Versorger</p>
              <p className="text-sm font-bold text-foreground mt-1">{company?.current_supplier || '–'}</p>
              {company?.current_price_ct_kwh && <p className="text-xs text-muted-foreground">{company.current_price_ct_kwh} ct/kWh</p>}
            </div>
          </div>
        </div>
      </div>

      {/* Upload section (if no analysis yet) */}
      {!analysis && (
        <div className="hk-card">
          <div className="hk-card-body">
            <div className="text-center mb-6">
              <div className="w-14 h-14 bg-accent rounded-xl flex items-center justify-center mx-auto mb-3">
                <BarChart3 className="text-primary" size={26} />
              </div>
              <h3 className="text-lg font-bold text-foreground">Noch praeziseres Angebot?</h3>
              <p className="text-sm text-muted-foreground mt-1 max-w-lg mx-auto">
                Laden Sie Ihren Lastgang hoch (15-Minuten-Werte) und wir analysieren Ihren Verbrauch im Detail — fuer bessere Preise und Optimierungsvorschlaege.
              </p>
            </div>

            <div
              onDragEnter={handleDrag} onDragLeave={handleDrag} onDragOver={handleDrag} onDrop={handleDrop}
              onClick={() => inputRef.current?.click()}
              className={`relative border-2 border-dashed rounded-xl p-10 text-center cursor-pointer transition-all ${dragActive ? 'border-primary bg-accent' : 'border-border hover:border-primary hover:bg-muted/50'}`}
            >
              <input ref={inputRef} type="file" accept=".csv,.xlsx,.xls" onChange={(e) => e.target.files?.[0] && processFile(e.target.files[0])} className="hidden" />
              {processing ? (
                <div>
                  <div className="w-10 h-10 border-2 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-3" />
                  <p className="text-sm font-semibold text-foreground">Lastgang wird analysiert...</p>
                </div>
              ) : (
                <>
                  <Upload className="mx-auto text-primary mb-3" size={32} />
                  <p className="font-bold text-foreground">Lastgang-Datei hier ablegen</p>
                  <p className="text-xs text-muted-foreground mt-1">oder klicken zum Auswaehlen</p>
                  <p className="text-[11px] text-muted-foreground mt-4">CSV oder Excel &middot; 15-Min-Intervalle &middot; ueblicherweise vom Verteilnetzbetreiber</p>
                </>
              )}
            </div>

            {error && (
              <div className="mt-4 p-3 bg-destructive/10 border border-destructive/20 rounded-lg flex items-start gap-2">
                <AlertCircle size={16} className="text-destructive flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-semibold text-destructive">Fehler beim Analysieren</p>
                  <p className="text-xs text-muted-foreground mt-0.5">{error}</p>
                </div>
              </div>
            )}

            <div className="mt-6 pt-6 border-t border-border grid grid-cols-1 md:grid-cols-3 gap-4 text-xs">
              <div className="flex items-start gap-2">
                <CheckCircle2 size={14} className="text-success flex-shrink-0 mt-0.5" />
                <span className="text-muted-foreground">Nur Sie sehen Ihre Daten — vertraulich und DSGVO-konform</span>
              </div>
              <div className="flex items-start gap-2">
                <CheckCircle2 size={14} className="text-success flex-shrink-0 mt-0.5" />
                <span className="text-muted-foreground">Auswertung in Sekunden — direkt im Browser</span>
              </div>
              <div className="flex items-start gap-2">
                <CheckCircle2 size={14} className="text-success flex-shrink-0 mt-0.5" />
                <span className="text-muted-foreground">Bessere Angebote durch Ihr individuelles Profil</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Analysis results */}
      {analysis && (
        <>
          {/* KPIs */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            <div className="stat-card">
              <p className="stat-label">Gesamtverbrauch</p>
              <p className="text-xl font-bold text-foreground mt-1">{Math.round(analysis.totalKwh).toLocaleString('de-DE')}</p>
              <p className="text-xs text-muted-foreground">kWh im Zeitraum</p>
            </div>
            <div className="stat-card">
              <p className="stat-label">Jahreshochrechnung</p>
              <p className="text-xl font-bold text-foreground mt-1">{Math.round(analysis.annualKwh).toLocaleString('de-DE')}</p>
              <p className="text-xs text-muted-foreground">kWh/Jahr</p>
            </div>
            <div className="stat-card">
              <p className="stat-label">Spitzenlast</p>
              <p className="text-xl font-bold text-foreground mt-1" style={{ color: '#cc0000' }}>{analysis.peakLoad.toFixed(1)}</p>
              <p className="text-xs text-muted-foreground">kW</p>
            </div>
            <div className="stat-card">
              <p className="stat-label">Grundlast</p>
              <p className="text-xl font-bold text-foreground mt-1" style={{ color: '#00a864' }}>{analysis.baseLoad.toFixed(1)}</p>
              <p className="text-xs text-muted-foreground">kW</p>
            </div>
            <div className="stat-card">
              <p className="stat-label">Peak-Anteil</p>
              <p className="text-xl font-bold text-foreground mt-1">{analysis.peakPercent.toFixed(0)}%</p>
              <p className="text-xs text-muted-foreground">8-20 Uhr</p>
            </div>
            <div className="stat-card">
              <p className="stat-label">Lastfaktor</p>
              <p className="text-xl font-bold text-foreground mt-1">{(analysis.loadFactor * 100).toFixed(0)}%</p>
              <p className="text-xs text-muted-foreground">Gleichmaessigkeit</p>
            </div>
          </div>

          {/* Interpretation */}
          <div className="hk-card">
            <div className="hk-card-header">
              <h6>Was sagen uns Ihre Daten?</h6>
            </div>
            <div className="hk-card-body">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {analysis.peakPercent > 60 && (
                  <div className="flex items-start gap-3 p-3 bg-[#fff8e0] rounded-lg">
                    <Sun size={18} className="text-[#b8860b] flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-sm font-semibold">Hoher Tagesverbrauch</p>
                      <p className="text-xs text-muted-foreground mt-0.5">{analysis.peakPercent.toFixed(0)}% Ihres Verbrauchs faellt in Peak-Zeiten. Fuer Tagarbeiter typisch.</p>
                    </div>
                  </div>
                )}
                {analysis.loadFactor > 0.6 && (
                  <div className="flex items-start gap-3 p-3 bg-[#e6fbf2] rounded-lg">
                    <CheckCircle2 size={18} className="text-[#00a864] flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-sm font-semibold">Gleichmaessiger Verbrauch</p>
                      <p className="text-xs text-muted-foreground mt-0.5">Ihr Lastfaktor von {(analysis.loadFactor * 100).toFixed(0)}% ist gut — das ermoeglicht bessere Grosshandelspreise.</p>
                    </div>
                  </div>
                )}
                {analysis.loadFactor < 0.4 && (
                  <div className="flex items-start gap-3 p-3 bg-[#e8fcfd] rounded-lg">
                    <Info size={18} className="text-[#0ea5b8] flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-sm font-semibold">Hohe Lastspitzen</p>
                      <p className="text-xs text-muted-foreground mt-0.5">Ihr Profil schwankt stark. Lastmanagement koennte bis zu 10% Ersparnis bringen.</p>
                    </div>
                  </div>
                )}
                <div className="flex items-start gap-3 p-3 bg-accent rounded-lg">
                  <Zap size={18} className="text-primary flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-semibold">Individueller Beschaffungsprofil-Vorteil</p>
                    <p className="text-xs text-muted-foreground mt-0.5">Mit Ihrem Lastgang koennen wir bis zu 15% bessere Konditionen aushandeln als bei Standardprofilen.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Charts */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
            <div className="hk-card">
              <div className="hk-card-header"><h6>Verbrauch nach Tageszeit</h6></div>
              <div className="hk-card-body">
                <div style={{ height: 280 }}>
                  <ReactECharts option={hourlyChartOption} style={{ height: '100%', width: '100%' }} opts={{ renderer: 'svg' }} />
                </div>
                <p className="text-xs text-muted-foreground text-center mt-2">Peak (orange) vs. Off-Peak (teal)</p>
              </div>
            </div>

            <div className="hk-card">
              <div className="hk-card-header"><h6>Verbrauch nach Wochentag</h6></div>
              <div className="hk-card-body">
                <div style={{ height: 280 }}>
                  <ReactECharts option={weekdayChartOption} style={{ height: '100%', width: '100%' }} opts={{ renderer: 'svg' }} />
                </div>
              </div>
            </div>
          </div>

          {/* Heatmap */}
          <div className="hk-card">
            <div className="hk-card-header">
              <h6>Verbrauchs-Heatmap</h6>
              <span className="text-xs text-muted-foreground">Woche x Tageszeit</span>
            </div>
            <div className="hk-card-body">
              <div style={{ height: 320 }}>
                <ReactECharts option={heatmapOption} style={{ height: '100%', width: '100%' }} opts={{ renderer: 'svg' }} />
              </div>
            </div>
          </div>

          {/* Reset */}
          <div className="text-center">
            <button
              onClick={() => { setAnalysis(null); setUploadedFile(null); }}
              className="text-sm text-muted-foreground hover:text-primary underline"
            >
              Andere Datei hochladen
            </button>
          </div>
        </>
      )}
    </div>
  );
};

export default PortalConsumption;
